/* eslint-disable @typescript-eslint/no-explicit-any */
import { EyeIcon, ArrowUp, ArrowDown, Trash2, Pencil, ExternalLink } from 'lucide-react'; // 🔹 tambah ikon ExternalLink
import { useCallback, useState } from 'react';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import classNames from 'classnames';
import TablePagination from './TablePagination';
import TableHeader from './TableHeader';
import TableLoadingState from './TableLoadingState';
import getNestedValue from '@/utils/functions/getNestedValue';
import TableEmptyState from './TableEmptyState';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import UpdateRoleModal from './UpdateRoleModal';

export interface PaginatedData {
  content: { [key: string]: unknown }[];
  totalElements: number;
  totalPages: number;
}

export interface MainTableColumn {
  name: string;
  label: string;
  className?: string;
  render?: (value: any, row: any, index: number) => React.ReactNode;
}

interface MainTableProps {
  title?: string;
  showHeader?: boolean;
  isLoading?: boolean;
  data: PaginatedData;
  pageConfig: DataConfig;
  setPageConfig: React.Dispatch<React.SetStateAction<DataConfig>>;
  columns: MainTableColumn[];
  showActions?: boolean;
  hideView?: boolean;
  redirectIdTaskOnly?: boolean;
  visibleColumns?: Set<string>;
  setVisibleColumns?: (columns: Set<string>) => void;
  variableCols?: MainTableColumn[];
  refetch?: () => void;
  handleExport?: () => void;
  isLoadingMutate?: boolean;
  extraAction?: React.ReactNode;
  handleDelete?: (row: any) => void;
  showDeleteAction?: boolean | ((row: any) => boolean);
  onDelete?: (row: any) => Promise<void> | void;
  showEditRoleAction?: boolean | ((row: any) => boolean);
  onUpdateRole?: (member: any, newRole: string) => void;
  showEditAction?: boolean | ((row: any) => boolean);
  onEdit?: (row: any) => void;
  onView?: (row: any) => void;
  useConfirmDelete?: boolean;
  showExternalLink?: boolean | ((row: any) => boolean);
  externalLinkUrl?: (row: any) => string;
}

export default function MainTable({
  title,
  showHeader = true,
  isLoading = false,
  data = { content: [], totalElements: 0, totalPages: 0 },
  pageConfig,
  setPageConfig,
  columns,
  showActions = true,
  hideView = false,
  redirectIdTaskOnly = false,
  visibleColumns,
  setVisibleColumns,
  variableCols,
  handleExport,
  isLoadingMutate,
  extraAction,
  onDelete,
  showDeleteAction = true,
  onUpdateRole,
  showEditRoleAction = true,
  showEditAction = true,
  onEdit,
  onView,
  useConfirmDelete = true,
  showExternalLink = false,
  externalLinkUrl,
}: Readonly<MainTableProps>) {
  const handlePageChange = useCallback((newConfig: React.SetStateAction<DataConfig>) => {
    setPageConfig(newConfig);
  }, []);

  const handleSort = useCallback(
    (columnName: string) => {
      const isCurrentlySorted = pageConfig.orderBy === columnName;
      let newOrder: 'asc' | 'desc';
      if (isCurrentlySorted && pageConfig.order === 'asc') {
        newOrder = 'desc';
      } else {
        newOrder = 'asc';
      }

      setPageConfig(prev => ({
        ...prev,
        orderBy: columnName,
        order: newOrder,
        page: 0,
      }));
    },
    [pageConfig.orderBy, pageConfig.order, setPageConfig],
  );

  const getSortIcon = (columnName: string) => {
    if (pageConfig.orderBy !== columnName) return null;
    return pageConfig.order === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const openDeleteModal = (row: any) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (onDelete && selectedRow) {
      await onDelete(selectedRow);
    }
    setDeleteModalOpen(false);
    setSelectedRow(null);
  };

  const [isUpdateRoleOpen, setIsUpdateRoleOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);

  const openUpdateRoleModal = (member: any) => {
    setSelectedMember(member);
    setIsUpdateRoleOpen(true);
  };

  const handleUpdateRole = (newRole: string) => {
    if (onUpdateRole && selectedMember) {
      onUpdateRole(selectedMember, newRole);
    }
  };

  return (
    <section className="bg-white rounded pb-10 w-full grow">
      {showHeader && (
        <TableHeader
          title={title ?? ''}
          hideView={hideView}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          variableCols={variableCols}
          handleExport={handleExport}
          isLoadingMutate={isLoadingMutate}
          extraAction={extraAction}
        />
      )}
      <div className="overflow-x-hidden px-5 mt-5">
        <div className="rounded-lg overflow-x-hidden border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left table-auto rounded-lg text-gray-500">
              <thead className="text-xs text-[#717680] bg-[#ECF0F7]">
                <tr>
                  <th scope="col" className="p-3 w-4 text-center">
                    No
                  </th>
                  {columns
                    .filter(col => !visibleColumns || visibleColumns.has(col.name))
                    .map((column, index) => (
                      <th
                        key={column.name + index}
                        scope="col"
                        className={`p-3 min-w-32 max-w-60 ${column.className || ''}`}
                      >
                        <button
                          className={classNames('flex items-center gap-1 hover:text-gray-900 cursor-pointer', {
                            'font-medium text-primary hover:text-blue-700': pageConfig.orderBy === column.name,
                          })}
                          onClick={() => handleSort(column.name)}
                        >
                          {column.label}
                          {getSortIcon(column.name)}
                        </button>
                      </th>
                    ))}

                  {showActions && (
                    <th scope="col" className="text-center">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {isLoading && <TableLoadingState pageConfig={pageConfig} columns={columns} showActions={showActions} />}

                {!isLoading && !data?.content?.length && (
                  <TableEmptyState colSpan={columns.length + (showActions ? 2 : 1)} />
                )}

                {!isLoading &&
                  Array.isArray(data?.content) &&
                  data.content.filter(Boolean).map((request: any, index: number) => (
                    <tr className="bg-white border-b" key={request.id ?? index}>
                      <td className="p-3 text-center">{pageConfig.page * pageConfig.limit + index + 1}</td>
                      {columns
                        .filter(col => !visibleColumns || visibleColumns.has(col.name))
                        .map((column, indexCol) => (
                          <td key={column.name + indexCol} className="p-3">
                            <span className={`line-clamp-3 text-ellipsis ${column.className || ''}`}>
                              {column.render
                                ? column.render(getNestedValue(request, column.name), request, index)
                                : getNestedValue(request, column.name) || '-'}
                            </span>
                          </td>
                        ))}
                      {showActions && (
                        <td className="py-3 flex justify-center items-center gap-x-4">
                          {onUpdateRole && (
                            <button className="text-blue-600" onClick={() => openUpdateRoleModal(request)}>
                              Edit Role
                            </button>
                          )}

                          {onDelete &&
                            (typeof showDeleteAction === 'function' ? showDeleteAction(request) : showDeleteAction) && (
                            <button
                              className="text-red-500"
                              onClick={() => {
                                if (useConfirmDelete) {
                                  openDeleteModal(request);
                                } else if (onDelete) {
                                  onDelete(request);
                                }
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}

                          {onEdit &&
                            (typeof showEditAction === 'function' ? showEditAction(request) : showEditAction) && (
                            <button className="text-blue-600" onClick={() => onEdit(request)}>
                              <Pencil size={16} />
                            </button>
                          )}

                          {onView && (
                            <button className="text-primary" onClick={() => onView(request)}>
                              <EyeIcon size={16} />
                            </button>
                          )}

                          {showExternalLink &&
                            (typeof showExternalLink === 'function'
                              ? showExternalLink(request)
                              : showExternalLink) && (
                            <a
                              href={externalLinkUrl ? externalLinkUrl(request) : '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-500 hover:text-gray-400"
                            >
                              <ExternalLink size={16} />
                            </a>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={pageConfig.page}
            totalPages={data?.totalPages || 0}
            onPageChange={page => handlePageChange(prev => ({ ...prev, page }))}
            disabled={isLoading}
          />
        </div>
      </div>

      {useConfirmDelete && (
        <ConfirmDeleteModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      )}

      <UpdateRoleModal
        open={isUpdateRoleOpen}
        onClose={() => setIsUpdateRoleOpen(false)}
        onSubmit={handleUpdateRole}
        currentRole={selectedMember?.role}
      />
    </section>
  );
}
